import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';

import { getUserUuidHeaders } from '../utils/httpUtils';
import { processApiPath } from '../utils/url';

interface QueryParams {
    [key: string]: any | undefined;
}

interface FetchClassDataProps<T> {
    userUuid: string;
    apiPath: string;
    params?: QueryParams;
}

type useApiEndpointGetProps<T, R = T> = FetchClassDataProps<T> & {
    userUuid: string;
    apiPath: string;
    enabled: boolean;
    params?: QueryParams;
    queryKeyAttributes?: any[]; // Optional attributes to be used as query key
    useQueryOptions?: Omit<UseQueryOptions<R, any, R, any>, 'queryFn' | 'enabled' | 'queryKey'>;
    transformer?: (data: T) => R; // Optional transformer function
    withoutAuthentication?: boolean; // Optional flag to skip authentication headers
};

/**
 * Fetches class data from the API.
 * @param {FetchClassDataProps<T> & { withoutAuthentication?: boolean }} props - The properties for fetching class data.
 * @returns {Promise<T>} - The fetched class data.
 */

const fetchClassData = async <T>({
    userUuid,
    apiPath,
    params,
    withoutAuthentication
}: FetchClassDataProps<T> & { withoutAuthentication?: boolean }): Promise<T> => {
    const headers = withoutAuthentication ? {} : getUserUuidHeaders(userUuid);
    const response = await axios.get<T>(processApiPath(apiPath), {
        headers,
        params: { ...params }
    });
    return response.data;
};

/**
 * A custom React hook to fetch data from a class API endpoint using React Query.
 *
 * @template T - The type of the data returned by the API.
 * @template R - The type of the transformed data.
 * @param {useApiEndpointGetProps<T, R>} props - The properties for the hook.
 * @param {string} props.userUuid - The UUID of the user, used to generate authentication headers.
 * @param {string} props.apiPath - The API path to fetch data from.
 * @param {boolean} props.enabled - Whether the query should be enabled. Query will also wait for userUuid to be initialized unless `withoutAuthentication` is true.
 * @param {QueryParams} [props.params] - Optional query parameters for the API call.
 * @param {Omit<UseQueryOptions<R, any, R, any>, 'queryFn' | 'enabled' | 'queryKey'>} [props.useQueryOptions] - Additional options for the React Query `useQuery` hook.
 * @param {(data: T) => R} [props.transformer] - Optional transformer function to process the response data.
 * @param {any[]} [props.queryKeyAttributes] - Optional attributes to be included in the query key. These attributes will be appended to the `apiPath` to uniquely identify the query.
 * @param {boolean} [props.withoutAuthentication] - If true, the API will be called without requiring `userUuid` and no authentication headers will be sent.
 * @returns {UseQueryResult<R, any>} - The result of the query, including transformed data, status, and error.
 *
 * @example
 * const { data, isLoading, error } = useApiEndpointGet({
 *   userUuid: '1234-5678',
 *   apiPath: 'classes',
 *   enabled: true,
 *   params: { filter: 'active' },
 *   transformer: (data) => data.map(item => item.name),
 *   queryKeyAttributes: ['active'],
 *   useQueryOptions: { staleTime: 5000 }
 * });
 *
 * @remarks
 * If the type `R` is omitted, the hook will default to using the type `T` for the returned data.
 * This is useful when no transformation is needed, and the API response can be used as-is.
 * The `queryKey` is constructed as an array starting with `apiPath` and optionally followed by `queryKeyAttributes`.
 * For example, if `apiPath` is `'classes'` and `queryKeyAttributes` is `['active']`, the `queryKey` will be `['classes', 'active']`.
 */
export const useApiEndpointGet = <T, R = T>(props: useApiEndpointGetProps<T, R>): UseQueryResult<R, any> => {
    const { userUuid, apiPath, enabled, params, transformer, queryKeyAttributes, withoutAuthentication } = props;
    const queryKey = [apiPath];
    if (queryKeyAttributes) {
        queryKey.push(...queryKeyAttributes.filter((attr) => attr !== undefined));
    }
    return useQuery<R, any>({
        ...props.useQueryOptions,
        enabled: (withoutAuthentication || !!userUuid) && !!enabled,
        queryKey: [apiPath, queryKeyAttributes],
        queryFn: async () => {
            const data = await fetchClassData<T>({ userUuid, apiPath, params, withoutAuthentication });
            return transformer ? transformer(data) : (data as unknown as R);
        }
    });
};
